import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import type { Im } from '../api/ims.js';
import type { AuthUser } from './RequireAuth.js';

/**
 * Who is signed in and who else is on the team, for every view: whether this
 * person may change a deal (the server enforces it; this only decides what to
 * offer), and the names to show for deal owners.
 */

export interface TeamMember {
  email: string;
  name: string;
  first_name: string;
  role: 'admin' | 'member';
  /** The address the agent emails from on their deals. */
  sends_from: string;
  /** Google profile photo, once they have signed in. */
  picture_url: string | null;
}

/** The author of notes written through the API: Claude, not a teammate. */
export const CLAUDE_AUTHOR = 'claude';

interface SessionValue {
  user: AuthUser;
  isAdmin: boolean;
  team: TeamMember[];
  /** Whether the signed-in person may change this deal. */
  canEdit: (im: Pick<Im, 'owner_email'>) => boolean;
  /** Whether this deal is the signed-in person's. */
  isMine: (im: Pick<Im, 'owner_email'>) => boolean;
  /** A short name for an owner ("Nils"), or null when unclaimed. */
  ownerName: (email: string | null) => string | null;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ user, children }: { user: AuthUser; children: ReactNode }) {
  const [team, setTeam] = useState<TeamMember[]>([]);

  useEffect(() => {
    let live = true;
    fetch('/api/users')
      .then((res) => (res.ok ? (res.json() as Promise<TeamMember[]>) : []))
      .then((rows) => {
        if (live) setTeam(rows);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const value = useMemo<SessionValue>(() => {
    const me = user.email.toLowerCase();
    const isAdmin = user.role === 'admin';
    return {
      user,
      isAdmin,
      team,
      canEdit: (im) => isAdmin || im.owner_email === me,
      isMine: (im) => im.owner_email === me,
      ownerName: (email) => {
        if (!email) return null;
        if (email === CLAUDE_AUTHOR) return 'Claude';
        const member = team.find((m) => m.email === email);
        return member?.first_name ?? email.split('@')[0];
      },
    };
  }, [user, team]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}

/** A small round badge with someone's initial, coloured per person. */
export function OwnerBadge({
  email,
  name,
  size = 'sm',
}: {
  email: string | null;
  name: string | null;
  size?: 'sm' | 'md';
}) {
  const dims = size === 'md' ? 'h-7 w-7 text-xs' : 'h-5 w-5 text-[10px]';
  if (!email) {
    return (
      <span
        title="Unclaimed"
        className={`${dims} inline-flex shrink-0 items-center justify-center rounded-full border border-dashed border-ink-300/60 text-ink-300`}
      >
        ?
      </span>
    );
  }
  return (
    <span
      title={name ?? email}
      className={`${dims} inline-flex shrink-0 items-center justify-center rounded-full font-semibold text-ink-900 ${ownerColour(email)}`}
    >
      {(name ?? email).charAt(0).toUpperCase()}
    </span>
  );
}

const PALETTE = ['bg-sky-300', 'bg-amber-300', 'bg-emerald-300', 'bg-fuchsia-300', 'bg-rose-300'];

function ownerColour(email: string): string {
  let hash = 0;
  for (const ch of email) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[hash % PALETTE.length];
}

/**
 * Someone's face: their Google photo when we have one, else the coloured
 * initial. Claude gets its own mark, so its notes are never mistaken for a
 * teammate's.
 */
export function Avatar({ author, size = 'md' }: { author: string | null; size?: 'sm' | 'md' }) {
  const { team, ownerName } = useSession();
  const [broken, setBroken] = useState(false);
  const dims = size === 'md' ? 'h-7 w-7 text-xs' : 'h-5 w-5 text-[10px]';

  if (author === CLAUDE_AUTHOR) {
    return (
      <span
        title="Claude"
        className={`${dims} inline-flex shrink-0 items-center justify-center rounded-full bg-[#d97757] font-semibold text-white`}
      >
        ✳
      </span>
    );
  }
  const picture = team.find((m) => m.email === author)?.picture_url;
  if (author && picture && !broken) {
    return (
      <img
        src={picture}
        alt={ownerName(author) ?? author}
        title={ownerName(author) ?? author}
        referrerPolicy="no-referrer"
        onError={() => setBroken(true)}
        className={`${dims} shrink-0 rounded-full object-cover`}
      />
    );
  }
  return <OwnerBadge email={author} name={ownerName(author)} size={size} />;
}
